import classnames from 'classnames'
import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import type { Area, CropperProps } from 'react-easy-crop'
import CropperImport from 'react-easy-crop'
import { Modal } from '../Modal/Modal'
import { getCroppedImg } from './cropImage'
import { type ImageShape } from './models'

const Cropper = CropperImport as unknown as React.ComponentType<Partial<CropperProps>>

const MAX_ZOOM = 3

type ImageCropModalProps = {
  imageSrc: string
  fileName: string
  sourceType: string | null
  shape: ImageShape
  title: string
  saveTitle: string
  minHeight?: number
  maxDimension?: number
  onCancel: () => void
  onConfirm: (blob: Blob) => void
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageSrc,
  fileName,
  sourceType,
  shape,
  title,
  saveTitle,
  minHeight,
  maxDimension,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [mediaAspect, setMediaAspect] = useState<number | undefined>(undefined)
  const [maxZoom, setMaxZoom] = useState(MAX_ZOOM)
  const [saving, setSaving] = useState(false)

  const isCircle = shape === 'circle'

  const handleMediaLoaded = ({ naturalWidth, naturalHeight }: { naturalWidth: number; naturalHeight: number }) => {
    setMediaAspect(naturalWidth / naturalHeight)
    setMaxZoom(minHeight ? Math.min(MAX_ZOOM, Math.max(1, naturalHeight / minHeight)) : MAX_ZOOM)
  }

  const handleSave = async () => {
    if (!croppedAreaPixels) {
      return
    }
    setSaving(true)
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, sourceType, maxDimension, minHeight)
      onConfirm(blob)
    } finally {
      setSaving(false)
    }
  }

  const header = (
    <div>
      <h5 className='mb-1'>{title}</h5>
      <div className='paragraph-medium zocks-text-gray-600'>Drag to reposition. Use the slider to zoom.</div>
    </div>
  )

  const body = (
    <div className='d-flex flex-column gap-3'>
      <div className={classnames('ratio', isCircle ? 'ratio-1x1' : 'ratio-16x9')}>
        <Cropper
          image={imageSrc}
          cropShape={isCircle ? 'round' : 'rect'}
          aspect={isCircle ? 1 : mediaAspect}
          showGrid={false}
          crop={crop}
          zoom={zoom}
          maxZoom={maxZoom}
          onMediaLoaded={handleMediaLoaded}
          onCropChange={setCrop}
          onCropComplete={(_area, areaPixels) => setCroppedAreaPixels(areaPixels)}
          onZoomChange={setZoom}
        />
      </div>
      <div className='d-flex align-items-center justify-content-center gap-2 zocks-text-gray-600 paragraph-medium'>
        <i className='bi bi-file-earmark' />
        {fileName}
      </div>
      <div className='d-flex align-items-center gap-2'>
        <i className='bi bi-dash-lg' />
        <Form.Range min={1} max={maxZoom} step={0.1} value={zoom} disabled={maxZoom <= 1} onChange={(e) => setZoom(Number(e.target.value))} />
        <i className='bi bi-plus-lg' />
      </div>
    </div>
  )

  return (
    <Modal
      centered
      show
      onHide={onCancel}
      header={header}
      body={body}
      showFooterActions
      footerPrimaryActionTitle={saveTitle}
      footerPrimaryActionLoading={saving}
      onFooterPrimaryAction={handleSave}
      onFooterSecondaryAction={onCancel}
    />
  )
}
